import { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, MessageSquare, Calendar, DollarSign, Settings, LogOut,
  Menu, TrendingUp, Crown, X, ArrowLeft, Globe, FileText, Heart, Wallet, Receipt, ShoppingBag,
  Home, Newspaper,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ENLogo } from '@/components/ENLogo';
import { PaymentSettings } from '@/components/PaymentSettings';
import { MobileBottomNav } from '@/components/MobileNav';
import { useIsMobile } from '@/hooks/useIsMobile';
import EditProfile from '@/components/vendor/EditProfile';
import UpgradeAndAds from '@/components/vendor/UpgradeAndAds';
import AddService from '@/components/vendor/AddService';
import Items from '@/components/vendor/Items';
import Overview from '@/components/vendor/Overview';
import VendorHome from '@/components/vendor/VendorHome';
import VendorFeedTab from '@/components/vendor/VendorFeedTab';
import Services from '@/components/vendor/Services';
import Orders from '@/components/vendor/Orders';
import Earnings from '@/components/vendor/Earnings';
import SupportVendors from '@/components/vendor/SupportVendors';
import Analytics from '@/components/vendor/Analytics';
import Inquiries from '@/components/vendor/Inquiries';
import InquiryDetail from '@/components/vendor/InquiryDetail';
import OrderDetail from '@/components/orders/OrderDetail';
import FeedbackForm from '@/components/feedback/FeedbackForm';
import FoundingVendorTab from '@/components/vendor/FoundingVendorTab';
import FoundingNonProfitTab from '@/components/vendor/FoundingNonProfitTab';
import TransactionsPage from '@/components/TransactionsPage';
import MyPurchases from '@/components/vendor/MyPurchases';
import { Leaf } from 'lucide-react';
import { usePendingInquiries } from '@/hooks/usePendingInquiries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const baseSidebarLinks = [
  { path: '/dashboard/vendor/founding', label: 'Founding Vendor', icon: Crown, gold: true },
  { path: '/dashboard/vendor', label: 'Home', icon: Home, exact: true },
  { path: '/dashboard/vendor/feed', label: 'The Feed', icon: Newspaper },
  { path: '/dashboard/vendor/overview', label: 'Overview', icon: LayoutDashboard },
  { path: '/dashboard/vendor/services', label: 'Services', icon: Package },
  { path: '/dashboard/vendor/items', label: 'Items (Shop)', icon: ShoppingBag },
  { path: '/dashboard/vendor/inquiries', label: 'Inquiries', icon: FileText },
  { path: '/dashboard/vendor/orders', label: 'Orders', icon: Calendar },
  { path: '/dashboard/vendor/my-purchases', label: 'My Purchases', icon: ShoppingBag },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/dashboard/vendor/analytics', label: 'Analytics', icon: TrendingUp },
  { path: '/dashboard/vendor/earnings', label: 'Earnings', icon: DollarSign },
  { path: '/dashboard/vendor/transactions', label: 'My Transactions', icon: Receipt },
  { path: '/dashboard/vendor/payments', label: 'Payment Settings', icon: Wallet },
  { path: '/dashboard/vendor/upgrade', label: 'Upgrade & Ads', icon: Crown },
  { path: '/dashboard/vendor/support-vendors', label: 'Support Vendors', icon: Heart },
  { path: '/dashboard/vendor/feedback', label: 'Feedback', icon: MessageSquare },
  { path: '/dashboard/vendor/profile', label: 'Edit Profile', icon: Settings },
];

function VendorDashboardLayout({ children }) {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasWebsite, setHasWebsite] = useState(false);
  const [isNonProfit, setIsNonProfit] = useState(false);
  const isMobile = useIsMobile();
  const { count: pendingInquiriesCount } = usePendingInquiries();

  const mobileTabs = [
    { path: '/dashboard/vendor', label: 'Home', icon: Home, id: 'home', exact: true },
    { path: '/dashboard/vendor/feed', label: 'Feed', icon: Newspaper, id: 'feed' },
    { path: '/dashboard/vendor/overview', label: 'Overview', icon: LayoutDashboard, id: 'overview' },
    { path: '/messages', label: 'Messages', icon: MessageSquare, id: 'messages' },
    { path: '/dashboard/vendor/profile', label: 'Profile', icon: Settings, id: 'profile' },
  ];

  useEffect(() => {
    const checkWebsite = async () => {
      try {
        const response = await fetch(`${API}/builder/my-site`, { headers: getAuthHeaders() });
        if (response.ok) setHasWebsite(true);
      } catch {}
    };
    const checkVendorType = async () => {
      try {
        const r = await fetch(`${API}/vendors/me`, { headers: getAuthHeaders() });
        if (r.ok) {
          const v = await r.json();
          setIsNonProfit(v.vendor_type === 'nonprofit');
        }
      } catch {}
    };
    checkWebsite();
    checkVendorType();
  }, []);

  // Insert non-profit tab right after Founding Vendor when applicable
  const sidebarLinks = isNonProfit
    ? [
        baseSidebarLinks[0],
        { path: '/dashboard/vendor/founding-nonprofit', label: 'Founding Non-Profit', icon: Leaf, emerald: true },
        ...baseSidebarLinks.slice(1),
      ]
    : baseSidebarLinks;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const links = [
    { path: '/dashboard/vendor', label: 'Overview', icon: LayoutDashboard },
    { path: '/dashboard/vendor/services', label: 'My Services', icon: Package },
    { path: '/dashboard/vendor/items', label: 'Items (Shop)', icon: ShoppingBag },
    { path: '/messages', label: 'Messages', icon: MessageSquare },
    { path: '/dashboard/vendor/inquiries', label: 'Inquiries', icon: FileText },
    { path: '/dashboard/vendor/orders', label: 'Orders', icon: Calendar },
    { path: '/dashboard/vendor/earnings', label: 'Earnings', icon: DollarSign },
    { path: '/dashboard/vendor/analytics', label: 'Analytics', icon: TrendingUp },
    { path: '/dashboard/vendor/support-vendors', label: 'Support Vendors', icon: Heart },
    { path: '/dashboard/vendor/saved', label: 'Saved Vendors', icon: Heart },
    { path: '/dashboard/vendor/payments', label: 'Payments', icon: Wallet },
    { path: '/dashboard/vendor/upgrade', label: 'Upgrade & Ads', icon: Crown },
    { path: '/dashboard/vendor/feedback', label: 'Feedback', icon: MessageSquare },
    { path: '/dashboard/vendor/profile', label: 'Edit Profile', icon: Settings },
  ];

  return (
    <div className="en-purple min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white font-sans" data-testid="vendor-dashboard">
      {/* Background grid pattern */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Mobile Branded Header (with rainbow ENLogo + drawer menu) */}
      {isMobile && (
        <header className="sticky top-0 z-40 bg-[#0E0E23]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center justify-between" data-testid="mobile-branded-header">
          <button
            onClick={() => setSidebarOpen(true)}
            className="relative p-2 rounded-lg text-[#A78BFA] hover:bg-white/5"
            data-testid="mobile-drawer-toggle"
            aria-label="Open menu"
          >
            <Menu size={22} />
            {pendingInquiriesCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-400 text-[#0E0E23] text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(252,211,77,0.7)] animate-pulse border border-yellow-200"
                data-testid="mobile-hamburger-inquiry-badge"
                aria-label={`${pendingInquiriesCount} pending inquir${pendingInquiriesCount === 1 ? 'y' : 'ies'}`}
              >
                {pendingInquiriesCount > 9 ? '9+' : pendingInquiriesCount}
              </span>
            )}
          </button>
          <ENLogo size="sm" linkTo="/dashboard/vendor" />
          <Link to="/messages" className="relative p-2 rounded-lg text-[#A78BFA] hover:bg-white/5" data-testid="mobile-header-messages">
            <MessageSquare size={20} />
          </Link>
        </header>
      )}

      {/* Mobile Drawer Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-drawer-overlay"
        />
      )}

      {/* Mobile toggle — hidden when using bottom nav on mobile */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-[60] hidden bg-[#1E1E3A]/90 backdrop-blur-xl border border-white/10 p-2.5 rounded-xl text-white shadow-lg"
        data-testid="vendor-sidebar-toggle"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar — desktop always, mobile as drawer */}
      <aside
        className={`fixed left-0 top-0 h-screen w-[260px] bg-[#1E1E3A]/95 backdrop-blur-2xl border-r border-white/5 flex-col z-[55] transition-transform duration-300 ${
          isMobile
            ? sidebarOpen ? 'flex translate-x-0' : 'flex -translate-x-full'
            : 'hidden lg:flex'
        }`}
        data-testid="vendor-sidebar"
      >
        {/* Close button on mobile */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute top-3 right-3 p-2 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/5"
            data-testid="mobile-drawer-close"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
        {/* Logo — click goes to marketplace home */}
        <div className="px-6 pt-7 pb-6">
          <ENLogo size="md" linkTo="/" />
        </div>

        {/* Back to Marketplace */}
        <div className="px-4 mb-2">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all text-sm"
          >
            <ArrowLeft size={16} />
            Back to Marketplace
          </Link>
        </div>

        {/* Super Dashboard Toggle - shows if user has website */}
        {hasWebsite && (
          <div className="px-4 mb-4" data-testid="super-dashboard-toggle">
            <div className="bg-[#0E0E23]/60 rounded-xl p-1 flex gap-1">
              <div className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_12px_rgba(139,92,246,0.3)]">
                <LayoutDashboard size={14} /> Vendor
              </div>
              <button
                onClick={() => navigate(hasWebsite ? '/website-builder' : '/create-website')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#9CA3AF] hover:text-white transition-all"
                data-testid="switch-to-website-btn"
              >
                <Globe size={14} /> Website
              </button>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {sidebarLinks.map((link) => {
            const active = isActive(link.path);
            const gold = link.gold;
            const emerald = link.emerald;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-200 group relative ${
                  gold
                    ? (active
                        ? 'text-[#FCD34D] bg-gradient-to-r from-[#F59E0B]/25 to-[#D97706]/10 shadow-[0_0_22px_rgba(245,158,11,0.28)] border border-[#F59E0B]/40'
                        : 'text-[#FCD34D] hover:bg-[#F59E0B]/10 border border-[#F59E0B]/20')
                    : emerald
                      ? (active
                          ? 'text-[#34D399] bg-gradient-to-r from-[#10B981]/25 to-[#059669]/10 shadow-[0_0_22px_rgba(16,185,129,0.28)] border border-[#10B981]/40'
                          : 'text-[#34D399] hover:bg-[#10B981]/10 border border-[#10B981]/20')
                      : (active
                          ? 'text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                          : 'text-[#9CA3AF] hover:text-white hover:bg-white/5')
                }`}
                data-testid={`vendor-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {active && !gold && !emerald && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-[#8B5CF6] to-[#3B82F6] rounded-r-full" />}
                {gold && <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#FCD34D] animate-pulse shadow-[0_0_10px_#FCD34D]" />}
                {emerald && <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#34D399] animate-pulse shadow-[0_0_10px_#34D399]" />}
                <link.icon size={18} strokeWidth={active ? 2 : 1.5} className={gold ? 'text-[#FCD34D]' : emerald ? 'text-[#34D399]' : (active ? 'text-[#A78BFA]' : 'group-hover:text-white')} />
                <span className="flex-1">{link.label}</span>
                {link.label === 'Inquiries' && pendingInquiriesCount > 0 && (
                  <span
                    className="ml-auto min-w-[22px] h-5 px-1.5 rounded-full bg-yellow-400 text-[#0E0E23] text-[11px] font-bold flex items-center justify-center shadow-[0_0_12px_rgba(252,211,77,0.65)] border border-yellow-200"
                    style={{ animation: 'inquiry-badge-pulse 1.4s ease-in-out infinite' }}
                    data-testid="sidebar-inquiry-badge"
                  >
                    {pendingInquiriesCount > 99 ? '99+' : pendingInquiriesCount}
                  </span>
                )}
              </Link>
            );
          })}
          <style>{`
            @keyframes inquiry-badge-pulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 12px rgba(252,211,77,0.65); }
              50%      { transform: scale(1.12); box-shadow: 0 0 22px 4px rgba(252,211,77,0.85); }
            }
          `}</style>
        </nav>

        {/* User / Logout */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.picture ? (
              <img src={user.picture.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture} alt={user?.name} className="w-9 h-9 rounded-full object-cover border-2 border-[#8B5CF6]/40 shadow-[0_0_10px_rgba(139,92,246,0.2)]" data-testid="sidebar-profile-pic" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-sm font-bold shadow-[0_0_10px_rgba(139,92,246,0.3)]">
                {user?.name?.[0]?.toUpperCase() || 'V'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-[11px] text-[#9CA3AF]">Vendor</p>
            </div>
            <button onClick={handleLogout} className="text-[#9CA3AF] hover:text-[#FF6B6B] transition-colors" data-testid="vendor-sidebar-logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] min-h-screen relative">
        <div className="p-4 pb-24 lg:p-10 lg:pb-10">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav tabs={mobileTabs} />}
    </div>
  );
}



export default function VendorDashboard() {
  return (
    <VendorDashboardLayout>
      <Routes>
        <Route index element={<VendorHome />} />
        <Route path="feed" element={<VendorFeedTab />} />
        <Route path="overview" element={<Overview />} />
        <Route path="services" element={<Services />} />
        <Route path="services/new" element={<AddService />} />
        <Route path="services/:serviceId/edit" element={<AddService />} />
        <Route path="items" element={<Items />} />
        <Route path="inquiries" element={<Inquiries />} />
        <Route path="inquiries/:inquiryId" element={<InquiryDetail />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:orderId" element={<OrderDetail />} />
        <Route path="my-purchases" element={<MyPurchases />} />
        <Route path="earnings" element={<Earnings />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="payments" element={<PaymentSettings />} />
        <Route path="upgrade" element={<UpgradeAndAds />} />
        <Route path="founding" element={<FoundingVendorTab />} />
        <Route path="founding-nonprofit" element={<FoundingNonProfitTab />} />
        <Route path="support-vendors" element={<SupportVendors />} />
        <Route path="feedback" element={<FeedbackForm source="ongoing" />} />
        <Route path="profile" element={<EditProfile />} />
      </Routes>
    </VendorDashboardLayout>
  );
}
